import { registerPlugin } from '@capacitor/core';
import { LocalNotifications } from '@capacitor/local-notifications';
import { App as CapacitorApp } from '@capacitor/app';
import { isNativeApp } from '../utils/platformUtils';
import { getTodayDateKey } from '../utils/dateHelpers';
import storage from './storageService';

const BackgroundGeolocation = registerPlugin('BackgroundGeolocation');

const STORAGE_KEY_ACTIVE_ROUTE = 'tracker_active_route';
const STORAGE_KEY_HISTORY = 'tracker_history';

// Haversine formula (km)
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
    const toRad = p => p * Math.PI / 180;
    const R = 6371; // km
    const dLat = toRad(lat2 - lat1);
    const dLon = toRad(lon2 - lon1);
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
};

class Tracker {
    constructor() {
        this.isTracking = false;
        this.wakeLock = null;
        this.watcherId = null;
        this.webWatchId = null;

        this.route = [];
        this.startTime = null;
        this.startDateKey = null;
        this.distances = 0;
        this.waitingTime = 0;
        this.stopStartTime = null;
        this.stopStartLat = null;
        this.stopStartLng = null;
        this.stops = [];
        this.maxSpeed = 0;
        this.currentSpeed = 0;
        this.syncedKm = 0;
        this.syncedSeconds = 0;
        this.lastMotionTime = Date.now(); // Última vez que o GPS se deslocou > 50m

        this.listeners = [];
        this.inactivityInterval = null;

        // --- VARIÁVEIS DE OTIMIZAÇÃO (PERSISTÊNCIA EM LOTE) ---
        this.lastSaveTime = 0;
        this.saveTimeout = null;
        this.savePending = false;

        // Garante gravação imediata se o app for para segundo plano ou minimizado
        if (typeof window !== 'undefined') {
            window.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'hidden' && this.savePending) {
                    this.saveStatePhysical();
                }
            });
            window.addEventListener('pagehide', () => {
                if (this.savePending) this.saveStatePhysical();
            });
        }
        if (isNativeApp()) {
            CapacitorApp.addListener('appStateChange', (state) => {
                if (!state.isActive && this.savePending) {
                    this.saveStatePhysical();
                }
            });
        }
    }

    async hasAcceptedDisclosure() {
        return await storage.getItem('location_disclosure_accepted') === true;
    }

    async acceptDisclosure() {
        await storage.setItem('location_disclosure_accepted', true);
    }

    async initActiveRoute() {
        const savedState = await storage.getItem(STORAGE_KEY_ACTIVE_ROUTE);
        if (savedState) {
            this.route = savedState.route || [];
            this.startTime = savedState.startTime || null;
            this.startDateKey = savedState.startDateKey || null;
            this.distances = savedState.distances || 0;
            this.waitingTime = savedState.waitingTime || 0;
            this.stopStartTime = savedState.stopStartTime || null;
            this.stopStartLat = savedState.stopStartLat ?? null;
            this.stopStartLng = savedState.stopStartLng ?? null;
            this.stops = savedState.stops || [];
            this.maxSpeed = savedState.maxSpeed || 0;
            this.currentSpeed = savedState.currentSpeed || 0;
            this.syncedKm = savedState.syncedKm || 0;
            this.syncedSeconds = savedState.syncedSeconds || 0;
            this.lastMotionTime = savedState.lastMotionTime || Date.now();
        }
    }

    resetState() {
        if (this.saveTimeout) {
            clearTimeout(this.saveTimeout);
            this.saveTimeout = null;
        }
        this.savePending = false;

        this.isTracking = false;
        this.route = [];
        this.startTime = null;
        this.startDateKey = null;
        this.distances = 0;
        this.waitingTime = 0;
        this.stopStartTime = null;
        this.stopStartLat = null;
        this.stopStartLng = null;
        this.stops = [];
        this.maxSpeed = 0;
        this.currentSpeed = 0;
        this.syncedKm = 0;
        this.syncedSeconds = 0;
        this.lastMotionTime = Date.now();
        this.notifyListeners();
    }

    async saveState() {
        this.saveStateDebounced();
    }

    saveStateDebounced() {
        const now = Date.now();
        this.savePending = true;

        // Otimização de Performance e Bateria: Debounce de 8 segundos durante a rota ativa
        // Reduz em 87% o I/O síncrono no disco e consumo de CPU/bateria, com garantia de flush
        // imediato em eventos de parada, término de rota, minimização de app ou segundo plano.
        const DEBOUNCE_MS = 8000;

        if (now - this.lastSaveTime < DEBOUNCE_MS) {
            if (!this.saveTimeout) {
                this.saveTimeout = setTimeout(() => {
                    this.saveStatePhysical();
                }, DEBOUNCE_MS - (now - this.lastSaveTime));
            }
        } else {
            this.saveStatePhysical();
        }

        this.notifyListeners();
    }

    async saveStatePhysical() {
        if (this.saveTimeout) {
            clearTimeout(this.saveTimeout);
            this.saveTimeout = null;
        }

        this.lastSaveTime = Date.now();
        this.savePending = false;

        const stateToSave = {
            route: this.route,
            startTime: this.startTime,
            startDateKey: this.startDateKey,
            distances: this.distances,
            waitingTime: this.waitingTime,
            stopStartTime: this.stopStartTime,
            stopStartLat: this.stopStartLat,
            stopStartLng: this.stopStartLng,
            stops: this.stops,
            maxSpeed: this.maxSpeed,
            currentSpeed: this.currentSpeed,
            syncedKm: this.syncedKm,
            syncedSeconds: this.syncedSeconds,
            lastMotionTime: this.lastMotionTime
        };

        try {
            await storage.setItem(STORAGE_KEY_ACTIVE_ROUTE, stateToSave);
        } catch (err) {
            console.error('TrackingService: Erro ao gravar estado físico da rota:', err);
        }
    }

    notifyListeners() {
        const state = this.getState();
        this.listeners.forEach(cb => cb(state));
    }

    addListener(callback) {
        if (!this.listeners.includes(callback)) {
            this.listeners.push(callback);
        }
    }

    removeListener(callback) {
        this.listeners = this.listeners.filter(cb => cb !== callback);
    }

    // Versão leve do estado, sem copiar a rota inteira.
    // Usada por componentes que só precisam do tempo online (ex.: Acompanhar Ganhos).
    getTimeState() {
        return {
            isTracking: this.isTracking,
            startTime: this.startTime,
            startDateKey: this.startDateKey
        };
    }

    getState() {
        return {
            isTracking: this.isTracking,
            route: [...this.route],
            startTime: this.startTime,
            startDateKey: this.startDateKey,
            distances: this.distances,
            waitingTime: this.waitingTime,
            stopStartTime: this.stopStartTime,
            stops: [...this.stops],
            maxSpeed: this.maxSpeed,
            currentSpeed: this.currentSpeed,
            syncedKm: this.syncedKm,
            syncedSeconds: this.syncedSeconds,
            hasWakeLock: !!this.wakeLock,
            currentStopDuration: this.stopStartTime ? Math.floor((Date.now() - this.stopStartTime) / 1000) : 0
        };
    }

    async start(onUpdate) {
        if (this.isTracking) return;

        // Checa disclosure ANTES de mudar qualquer estado (evita ficar "travado" rastreando sem watcher)
        const accepted = await this.hasAcceptedDisclosure();
        if (isNativeApp() && !accepted) {
            console.warn('TrackingService: Disclosure não aceito. Abortando start.');
            return;
        }

        this.isTracking = true;

        await this.requestWakeLock();

        if (onUpdate) this.addListener(onUpdate);

        await this.initActiveRoute();
        if (!this.startTime) {
            this.startTime = Date.now();
            this.startDateKey = getTodayDateKey();
            this.lastMotionTime = Date.now();
            await this.saveStatePhysical();
        }

        this.startInactivityTimer();

        if (isNativeApp()) {
            // Garantir permissão de notificações no Android 13+ para o funcionamento correto do Foreground Service
            try {
                const notifPerms = await LocalNotifications.checkPermissions();
                if (notifPerms.display !== 'granted') {
                    await LocalNotifications.requestPermissions();
                }
            } catch (notifErr) {
                console.warn('TrackingService: Falha ao solicitar permissão de notificações:', notifErr);
            }

            const gpsConfig = await storage.getItem('relive_precision') || 'high';
            const distFilter = gpsConfig === 'economy' ? 50 : 10;

            try {
                this.watcherId = await BackgroundGeolocation.addWatcher(
                    {
                        backgroundMessage: "Gravando rota do seu turno.",
                        backgroundTitle: "Jornada Pro: Rastreador Ativo",
                        requestPermissions: true,
                        stale: false,
                        distanceFilter: distFilter
                    },
                    (location, error) => {
                        if (error) {
                            console.error('BG Geo Erro:', error);
                            return;
                        }
                        if (location) this.handleLocation(location);
                    }
                );
            } catch (err) {
                console.error('Erro ao iniciar BackgroundGeolocation:', err);
                this.isTracking = false;
                if (onUpdate) this.removeListener(onUpdate);
                if (this.uiInterval) { clearInterval(this.uiInterval); this.uiInterval = null; }
                if (this.inactivityInterval) { clearInterval(this.inactivityInterval); this.inactivityInterval = null; }
                await this.releaseWakeLock();
                this.notifyListeners();
                return;
            }
        } else {
            if ("geolocation" in navigator) {
                this.webWatchId = navigator.geolocation.watchPosition(
                    (pos) => {
                        const loc = {
                            latitude: pos.coords.latitude,
                            longitude: pos.coords.longitude,
                            speed: pos.coords.speed || 0,
                            accuracy: pos.coords.accuracy,
                            time: pos.timestamp
                        };
                        this.handleLocation(loc);
                    },
                    (err) => console.error('Web Geo Erro:', err),
                    { enableHighAccuracy: true, maximumAge: 0, timeout: 5000 }
                );
            }
        }

        // Timer de atualização da UI (para tempo de espera real-time).
        // 5s em vez de 1s: o cronômetro local da página já atualiza a cada 1s;
        // isso evita copiar o array gigante da rota 60x/min à toa.
        this.uiInterval = setInterval(() => {
            if (this.isTracking) this.notifyListeners();
        }, 5000);
    }

    startInactivityTimer() {
        if (this.inactivityInterval) clearInterval(this.inactivityInterval);
        this.inactivityInterval = setInterval(() => {
            this.checkInactivity();
        }, 30 * 60 * 1000); // Check a cada 30 minutos
    }

    async checkInactivity() {
        if (!this.isTracking) return;

        const now = Date.now();
        const inactiveMs = now - this.lastMotionTime;
        const FOUR_HOURS = 4 * 60 * 60 * 1000;

        if (inactiveMs > FOUR_HOURS) {
            console.warn('TrackingService: Inatividade detectada (>4h). Enviando lembrete.');
            if (isNativeApp()) {
                try {
                    await LocalNotifications.schedule({
                        notifications: [
                            {
                                id: 1001,
                                title: "Jornada Pro: Ainda Ativa?",
                                body: "Notamos que você está parado há bastante tempo. Não esqueça de encerrar se já terminou o turno!",
                                schedule: { at: new Date(Date.now() + 1000) },
                                sound: null,
                                attachments: null,
                                actionTypeId: "",
                                extra: null
                            }
                        ]
                    });
                } catch (err) {
                    console.error('Erro ao enviar notificação de inatividade:', err);
                }
            }
        }
    }

    // Atualiza a configuração de precisão em tempo real se estiver rastreando
    async updateConfig(newPrecision) {
        if (!isNativeApp()) return;

        // Se estiver rastreando, precisamos reiniciar o watcher para aplicar o novo filtro
        if (this.isTracking && this.watcherId) {
            const distFilter = newPrecision === 'economy' ? 50 : 10;

            try {
                // Remove o antigo
                await BackgroundGeolocation.removeWatcher({ id: this.watcherId });

                // Adiciona o novo com o novo filtro
                this.watcherId = await BackgroundGeolocation.addWatcher(
                    {
                        backgroundMessage: "Gravando rota do seu turno.",
                        backgroundTitle: "Jornada Pro: Rastreador Ativo",
                        requestPermissions: true,
                        stale: false,
                        distanceFilter: distFilter
                    },
                    (location, error) => {
                        if (error) {
                            console.error('BG Geo Erro:', error);
                            return;
                        }
                        if (location) this.handleLocation(location);
                    }
                );
                console.log(`TrackingService: Precisão atualizada para ${newPrecision} (Filter: ${distFilter}m)`);
            } catch (err) {
                console.error('Erro ao atualizar precisão do GPS:', err);
            }
        }
    }

    async requestWakeLock() {
        if ('wakeLock' in navigator && !this.wakeLock) {
            try {
                this.wakeLock = await navigator.wakeLock.request('screen');
                this.wakeLock.addEventListener('release', () => {
                    this.wakeLock = null;
                    this.notifyListeners();
                });
                this.notifyListeners();
                return true;
            } catch (err) {
                console.error('Wake Lock Erro:', err);
                return false;
            }
        }
        return false;
    }

    // Método para permitir toggle manual (usado pelo Widget/Page)
    async toggleWakeLock() {
        if (this.wakeLock) {
            await this.releaseWakeLock();
        } else {
            await this.requestWakeLock();
        }
    }

    async releaseWakeLock() {
        if (this.wakeLock) {
            await this.wakeLock.release();
            this.wakeLock = null;
            this.notifyListeners();
        }
    }

    handleLocation(location) {
        const now = Date.now();

        // --- FILTRO ANTI-DRIFT GPS ---
        // 1. Ignora pontos com precisão ruim (>50m) quando a info existe
        if (location.accuracy != null && location.accuracy > 50) {
            return;
        }

        let speedKmh = location.speed ? Math.max(0, location.speed * 3.6) : 0;
        // 2. Se o plugin nativo informar accuracy mas não speed, estima pela distância/tempo
        // 3. Ignora saltos absurdos (>300 km/h = erro de GPS, ex: túnel/sombra)
        if (this.route.length > 0) {
            const lastPoint = this.route[this.route.length - 1];
            const dist = calculateDistance(lastPoint.lat, lastPoint.lng, location.latitude, location.longitude);
            const dtSec = Math.max(1, (now - (lastPoint.timestamp || now)) / 1000);
            const impliedKmh = (dist / dtSec) * 3600;
            if (impliedKmh > 300) {
                return; // spike de GPS, descarta
            }
            // 4. Drift parado: deslocamento < 8m com velocidade < 5 km/h não soma KM nem cria ponto
            if (dist < 0.008 && speedKmh < 5) {
                this.currentSpeed = speedKmh;
                if (speedKmh < 3 && !this.stopStartTime) {
                    this.stopStartTime = now;
                    this.stopStartLat = location.latitude;
                    this.stopStartLng = location.longitude;
                }
                this.saveState();
                return;
            }
        }

        this.currentSpeed = speedKmh;
        if (speedKmh > this.maxSpeed) {
            this.maxSpeed = speedKmh;
        }

        const point = {
            lat: location.latitude,
            lng: location.longitude,
            timestamp: now,
            speed: speedKmh
        };

        if (this.route.length > 0) {
            const lastPoint = this.route[this.route.length - 1];
            const dist = calculateDistance(lastPoint.lat, lastPoint.lng, point.lat, point.lng);
            this.distances += dist;

            // Se moveu mais de 50 metros, reseta o tempo de inatividade
            if (dist > 0.05) {
                this.lastMotionTime = now;
            }
        }

        this.route.push(point);

        // Threshold de parada: velocidade < 3 km/h indica veículo estacionado ou parado.
        // waitingTime acumula TODAS as paradas >= 30s (semáforo conta como espera).
        // stops[] guarda marcadores só de paradas >= 120s (evita poluir o mapa).
        if (speedKmh < 3) {
            if (!this.stopStartTime) {
                this.stopStartTime = now;
                this.stopStartLat = point.lat;
                this.stopStartLng = point.lng;
            }
        } else {
            if (this.stopStartTime) {
                const diffSecs = Math.floor((now - this.stopStartTime) / 1000);
                if (diffSecs >= 30) {
                    this.waitingTime += diffSecs;
                }
                if (diffSecs >= 120) {
                    this.stops.push({ lat: this.stopStartLat ?? point.lat, lng: this.stopStartLng ?? point.lng, duration: diffSecs, endTimestamp: now });
                }
                this.stopStartTime = null;
                this.stopStartLat = null;
                this.stopStartLng = null;
            }
        }

        this.saveState();
    }

    async stop() {
        this.isTracking = false;

        if (isNativeApp() && this.watcherId) {
            await BackgroundGeolocation.removeWatcher({ id: this.watcherId });
            this.watcherId = null;
        } else if (this.webWatchId !== null) {
            navigator.geolocation.clearWatch(this.webWatchId);
            this.webWatchId = null;
        }

        if (this.uiInterval) {
            clearInterval(this.uiInterval);
            this.uiInterval = null;
        }

        if (this.inactivityInterval) {
            clearInterval(this.inactivityInterval);
            this.inactivityInterval = null;
        }

        await this.releaseWakeLock();

        if (this.stopStartTime) {
            const now = Date.now();
            const diffSecs = Math.floor((now - this.stopStartTime) / 1000);
            if (diffSecs >= 30) {
                this.waitingTime += diffSecs;
            }
            if (diffSecs >= 120) {
                const last = this.route[this.route.length - 1];
                if (last) this.stops.push({ lat: this.stopStartLat ?? last.lat, lng: this.stopStartLng ?? last.lng, duration: diffSecs, endTimestamp: now });
            }
            this.stopStartTime = null;
            this.stopStartLat = null;
            this.stopStartLng = null;
        }
        await this.saveStatePhysical();
    }

    async finishRoute() {
        await this.stop();
        if (this.route.length === 0) {
            await storage.removeItem(STORAGE_KEY_ACTIVE_ROUTE);
            this.resetState();
            return null;
        }

        const history = await storage.getItem(STORAGE_KEY_HISTORY) || [];
        const completedRoute = {
            id: Date.now().toString(),
            date: new Date().toISOString(),
            ...this.getState(),
            endTime: Date.now()
        };

        history.push(completedRoute);
        // Limita histórico a 50 rotas (evita estourar quota do storage)
        const trimmed = history.slice(-50);
        await storage.setItem(STORAGE_KEY_HISTORY, trimmed);

        await storage.removeItem(STORAGE_KEY_ACTIVE_ROUTE);
        this.resetState();
        return completedRoute;
    }

    async getHistory() {
        return (await storage.getItem(STORAGE_KEY_HISTORY)) || [];
    }

    async deleteHistoryItem(id) {
        const history = await storage.getItem(STORAGE_KEY_HISTORY) || [];
        const updated = history.filter(h => String(h.id) !== String(id));
        await storage.setItem(STORAGE_KEY_HISTORY, updated);
        return updated;
    }

    async restoreActiveState() {
        await this.initActiveRoute();

        // Se existe uma rota iniciada e não finalizada (tem startTime mas isTracking falso devido a restart),
        // significa que o app foi morto pelo SO e devemos retomar o rastreamento silenciosamente.
        if (this.startTime && !this.isTracking) {
            console.log('TrackingService: Rota ativa recuperada pós-restart. Retomando GPS...');
            await this.resumeTracking();
        }

        return this.getState();
    }

    async resumeTracking() {
        this.isTracking = true;
        await this.requestWakeLock();
        this.startInactivityTimer();

        if (isNativeApp()) {
            // Garantir permissão de notificações no Android 13+ para o funcionamento correto do Foreground Service
            try {
                const notifPerms = await LocalNotifications.checkPermissions();
                if (notifPerms.display !== 'granted') {
                    await LocalNotifications.requestPermissions();
                }
            } catch (notifErr) {
                console.warn('TrackingService: Falha ao solicitar permissão de notificações:', notifErr);
            }

            const gpsConfig = await storage.getItem('relive_precision') || 'high';
            const distFilter = gpsConfig === 'economy' ? 50 : 10;

            try {
                this.watcherId = await BackgroundGeolocation.addWatcher(
                    {
                        backgroundMessage: "Gravando rota do seu turno.",
                        backgroundTitle: "Jornada Pro: Rastreador Ativo",
                        requestPermissions: true,
                        stale: false,
                        distanceFilter: distFilter
                    },
                    (location, error) => {
                        if (error) {
                            console.error('BG Geo Erro:', error);
                            return;
                        }
                        if (location) this.handleLocation(location);
                    }
                );
            } catch (err) {
                console.error('Erro ao retomar BackgroundGeolocation:', err);
                this.isTracking = false;
            }
        } else {
            if ("geolocation" in navigator) {
                this.webWatchId = navigator.geolocation.watchPosition(
                    (pos) => {
                        const loc = {
                            latitude: pos.coords.latitude,
                            longitude: pos.coords.longitude,
                            speed: pos.coords.speed || 0,
                            accuracy: pos.coords.accuracy,
                            time: pos.timestamp
                        };
                        this.handleLocation(loc);
                    },
                    (err) => console.error('Web Geo Erro:', err),
                    { enableHighAccuracy: true, maximumAge: 0, timeout: 5000 }
                );
            }
        }

        if (this.uiInterval) clearInterval(this.uiInterval);
        this.uiInterval = setInterval(() => {
            if (this.isTracking) this.notifyListeners();
        }, 5000);
    }
}

export const TrackingService = new Tracker();
